import { useState, useCallback, useRef } from "react";
import { defaultRules, defaultRulesCTA, type RuleSection, type RulesCTAData } from "@/data/siteContent";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";

const ICON_OPTIONS = [
  "ri-shield-star-line", "ri-alert-line", "ri-user-star-line", "ri-money-dollar-circle-line",
  "ri-calendar-line", "ri-door-open-line", "ri-secure-payment-line", "ri-file-list-3-line",
  "ri-information-line", "ri-checkbox-circle-line", "ri-settings-3-line", "ri-heart-line",
  "ri-store-2-line", "ri-archive-line", "ri-layout-grid-line", "ri-map-pin-2-line",
  "ri-price-tag-3-line", "ri-shield-check-line", "ri-star-line", "ri-bookmark-line",
  "ri-lock-line", "ri-time-line", "ri-truck-line", "ri-gift-line",
];

const COLOR_OPTIONS = [
  { iconColor: "text-pink-500", iconBg: "bg-pink-50", label: "Pink" },
  { iconColor: "text-green-600", iconBg: "bg-green-50", label: "Green" },
  { iconColor: "text-amber-500", iconBg: "bg-amber-50", label: "Amber" },
  { iconColor: "text-sky-500", iconBg: "bg-sky-50", label: "Sky" },
  { iconColor: "text-violet-500", iconBg: "bg-violet-50", label: "Violet" },
  { iconColor: "text-rose-500", iconBg: "bg-rose-50", label: "Rose" },
];

const LIST_ICON_OPTIONS = [
  { icon: "ri-checkbox-circle-line", color: "text-green-400", label: "Check (green)" },
  { icon: "ri-close-circle-line", color: "text-pink-400", label: "X (pink)" },
  { icon: "ri-alert-line", color: "text-amber-400", label: "Alert (amber)" },
  { icon: "ri-information-line", color: "text-sky-400", label: "Info (sky)" },
  { icon: "ri-star-line", color: "text-yellow-400", label: "Star (yellow)" },
  { icon: "ri-arrow-right-circle-line", color: "text-gray-400", label: "Arrow (gray)" },
];

function IconPickerPopup({
  currentIcon,
  currentColor,
  currentBg,
  onSelect,
  onClose,
}: {
  currentIcon: string;
  currentColor: string;
  currentBg: string;
  onSelect: (icon: string, color: string, bg: string) => void;
  onClose: () => void;
}) {
  const [selectedIcon, setSelectedIcon] = useState(currentIcon);
  const [selectedColor, setSelectedColor] = useState(currentColor);
  const [selectedBg, setSelectedBg] = useState(currentBg);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 w-80 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-bold text-sm">Choose Icon & Color</h3>
        <div>
          <p className="text-gray-400 text-xs mb-2">Icon</p>
          <div className="flex flex-wrap gap-1.5">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setSelectedIcon(ic)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${selectedIcon === ic ? "bg-pink-500 text-white" : "bg-gray-700 text-gray-300 hover:bg-gray-600"}`}
              >
                <i className={`${ic} text-sm`}></i>
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-gray-400 text-xs mb-2">Color</p>
          <div className="flex flex-wrap gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c.label}
                onClick={() => { setSelectedColor(c.iconColor); setSelectedBg(c.iconBg); }}
                className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer border-2 transition-colors ${selectedColor === c.iconColor ? "border-white" : "border-transparent"} ${c.iconBg}`}
              >
                <i className={`${selectedIcon} ${c.iconColor} text-sm`}></i>
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => { onSelect(selectedIcon, selectedColor, selectedBg); onClose(); }}
            className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap"
          >
            Apply
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function ListIconPickerPopup({
  currentIcon,
  onSelect,
  onClose,
}: {
  currentIcon: string;
  onSelect: (icon: string, color: string) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState(currentIcon);
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 w-72 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-bold text-sm">Choose List Icon</h3>
        <div className="space-y-2">
          {LIST_ICON_OPTIONS.map((opt) => (
            <button
              key={opt.icon}
              onClick={() => setSelected(opt.icon)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selected === opt.icon ? "bg-pink-500/20 border border-pink-500" : "bg-gray-800 hover:bg-gray-700 border border-transparent"}`}
            >
              <i className={`${opt.icon} ${opt.color} text-lg`}></i>
              <span className="text-gray-300 text-sm">{opt.label}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => {
              const opt = LIST_ICON_OPTIONS.find((o) => o.icon === selected) || LIST_ICON_OPTIONS[0];
              onSelect(opt.icon, opt.color);
              onClose();
            }}
            className="bg-green-500 hover:bg-green-600 text-white text-sm font-bold px-4 py-2 rounded-full cursor-pointer whitespace-nowrap"
          >
            Apply
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 text-sm cursor-pointer whitespace-nowrap">Cancel</button>
        </div>
      </div>
    </div>
  );
}



interface SubsectionListStyle {
  icon: string;
  iconColor: string;
}

function RuleAccordionItem({
  section,
  isAdmin,
  isDark,
  onUpdateTitle,
  onUpdateSubHeading,
  onUpdateSubBody,
  onUpdateSubListItem,
  onAddListItem,
  onRemoveListItem,
  onAddSubsection,
  onRemoveSubsection,
  onRemoveSection,
  onUpdateIcon,
  onUpdateSubListIcon,
}: {
  section: RuleSection;
  isAdmin: boolean;
  isDark: boolean;
  onUpdateTitle: (val: string) => void;
  onUpdateSubHeading: (subIdx: number, val: string) => void;
  onUpdateSubBody: (subIdx: number, val: string) => void;
  onUpdateSubListItem: (subIdx: number, itemIdx: number, val: string) => void;
  onAddListItem: (subIdx: number) => void;
  onRemoveListItem: (subIdx: number, itemIdx: number) => void;
  onAddSubsection: () => void;
  onRemoveSubsection: (subIdx: number) => void;
  onRemoveSection: () => void;
  onUpdateIcon: (icon: string, color: string, bg: string) => void;
  onUpdateSubListIcon: (subIdx: number, icon: string, color: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [listIconPicker, setListIconPicker] = useState<{ subIdx: number } | null>(null);
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();

  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";
  const hoverBg = isDark ? "hover:bg-gray-700/50" : "hover:bg-pink-50/50";
  const titleColor = isDark ? "text-gray-100" : "text-gray-900";
  const bodyColor = isDark ? "text-gray-400" : "text-gray-600";
  const subHeadColor = isDark ? "text-gray-300" : "text-gray-800";
  const dividerColor = isDark ? "border-gray-700" : "border-pink-50";

  return (
    <>
      {showIconPicker && (
        <IconPickerPopup
          currentIcon={section.icon}
          currentColor={section.iconColor}
          currentBg={section.iconBg}
          onSelect={onUpdateIcon}
          onClose={() => setShowIconPicker(false)}
        />
      )}
      {listIconPicker !== null && (
        <ListIconPickerPopup
          currentIcon={section.subsections[listIconPicker.subIdx]?.listIcon || "ri-checkbox-circle-line"}
          onSelect={(icon, color) => { onUpdateSubListIcon(listIconPicker.subIdx, icon, color); }}
          onClose={() => setListIconPicker(null)}
        />
      )}
      <div className={`border rounded-2xl overflow-hidden transition-all ${cardBg}`}>
        <button
          onClick={() => setOpen((v) => !v)}
          className={`w-full flex items-center gap-4 px-6 py-5 text-left cursor-pointer transition-colors ${hoverBg}`}
        >
          <div className="relative group/icon flex-shrink-0">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${section.iconBg}`}>
              <i className={`${section.icon} ${section.iconColor} text-xl`}></i>
            </div>
            {isAdmin && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowIconPicker(true); }}
                className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl opacity-0 group-hover/icon:opacity-100 transition-opacity cursor-pointer"
                title="Change icon"
              >
                <i className="ri-edit-line text-white text-xs"></i>
              </button>
            )}
          </div>
          {isAdmin ? (
            <div className="flex-1" onClick={(e) => e.stopPropagation()}>
              <AdminEditField value={section.title} onSave={onUpdateTitle} inputClassName="font-bold text-base">
                <span className={`text-base font-bold ${titleColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{section.title}</span>
              </AdminEditField>
            </div>
          ) : (
            <span className={`flex-1 text-base font-bold ${titleColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>{section.title}</span>
          )}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemoveSection(); }}
                className="w-7 h-7 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 transition-colors"
                title="Delete this section"
              >
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            )}
            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
              <i className={`ri-arrow-down-s-line text-gray-400 text-xl transition-transform duration-300 ${open ? "rotate-180" : ""}`}></i>
            </div>
          </div>
        </button>

        {open && (
          <div className={`px-6 pb-6 border-t ${dividerColor}`}>
            <div className="pt-5 space-y-5">
              {section.subsections.map((sub, si) => (
                <div key={si} className="space-y-2 relative group/subsection">
                  {/* Delete subsection button (admin only) */}
                  {isAdmin && (
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}>Block {si + 1} of {section.subsections.length}</span>
                      </div>
                      <button
                        onClick={() => onRemoveSubsection(si)}
                        className="flex items-center gap-1 text-[10px] font-bold bg-red-50 hover:bg-red-100 text-red-400 hover:text-red-600 px-2 py-0.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
                        title="Delete this subsection"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                        Delete block
                      </button>
                    </div>
                  )}
                  {(sub.heading || isAdmin) && sub.heading !== undefined && (
                    isAdmin ? (
                      <div className="flex items-center gap-1">
                        <AdminEditField
                          value={sub.heading || ""}
                          onSave={(v) => onUpdateSubHeading(si, v)}
                          inputClassName="text-sm font-bold uppercase tracking-wide"
                        >
                          {sub.heading ? (
                            <h4 className={`text-sm font-bold uppercase tracking-wide ${subHeadColor}`}>{sub.heading}</h4>
                          ) : (
                            <span className="text-xs text-gray-400 italic">+ Add sub-heading</span>
                          )}
                        </AdminEditField>
                      </div>
                    ) : (
                      sub.heading && (
                        <h4 className={`text-sm font-bold uppercase tracking-wide ${subHeadColor}`}>{sub.heading}</h4>
                      )
                    )
                  )}

                  {sub.body && (
                    isAdmin ? (
                      <AdminEditField value={sub.body} onSave={(v) => onUpdateSubBody(si, v)} multiline richText inputClassName="text-sm">
                        <div className="space-y-2">
                          {sub.body.split("\n\n").map((para, pi) => (
                            <p key={pi} className={`text-sm leading-relaxed whitespace-pre-line ${bodyColor}`}>{para}</p>
                          ))}
                        </div>
                      </AdminEditField>
                    ) : (
                      <div className="space-y-2">
                        {sub.body.split("\n\n").map((para, pi) => (
                          <p key={pi} className={`text-sm leading-relaxed whitespace-pre-line ${bodyColor}`}>{para}</p>
                        ))}
                      </div>
                    )
                  )}

                  {sub.list && sub.list.length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {isAdmin && (
                        <li className="mb-2">
                          <button
                            onClick={() => setListIconPicker({ subIdx: si })}
                            className="flex items-center gap-2 text-xs text-pink-400 hover:text-pink-600 cursor-pointer border border-dashed border-pink-200 rounded-lg px-3 py-1.5"
                          >
                            <i className={`${(sub as SubsectionListStyle & typeof sub).listIcon || (sub.listStyle === "warning" ? "ri-close-circle-line" : "ri-checkbox-circle-line")} text-sm`}></i>
                            Change list icon
                          </button>
                        </li>
                      )}
                      {sub.list.map((item, li) => {
                        const listIcon = (sub as SubsectionListStyle & typeof sub).listIcon || (sub.listStyle === "warning" ? "ri-close-circle-line" : "ri-checkbox-circle-line");
                        const listIconColor = (sub as SubsectionListStyle & typeof sub).listIconColor || (sub.listStyle === "warning" ? "text-pink-400" : "text-green-400");
                        return (
                          <li key={li} className={`flex items-start gap-2 text-sm ${bodyColor}`}>
                            <i className={`${listIcon} ${listIconColor} mt-0.5 flex-shrink-0 text-base`}></i>
                            {isAdmin ? (
                              <div className="flex items-center gap-1 flex-1">
                                <AdminEditField value={item} onSave={(v) => onUpdateSubListItem(si, li, v)} className="flex-1" inputClassName="text-sm">
                                  <span>{item}</span>
                                </AdminEditField>
                                <button onClick={() => onRemoveListItem(si, li)} className="w-5 h-5 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer flex-shrink-0" title="Remove">
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </div>
                            ) : (
                              <span>{item}</span>
                            )}
                          </li>
                        );
                      })}
                      {isAdmin && (
                        <li>
                          <button onClick={() => onAddListItem(si)} className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1">
                            <i className="ri-add-line"></i> Add item
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                  {isAdmin && (!sub.list || sub.list.length === 0) && (
                    <button onClick={() => onAddListItem(si)} className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1">
                      <i className="ri-add-line"></i> Add list item
                    </button>
                  )}
                </div>
              ))}
              {isAdmin && (
                <button
                  onClick={onAddSubsection}
                  className="text-xs text-green-500 hover:text-green-700 flex items-center gap-1 cursor-pointer border border-dashed border-green-300 rounded-lg px-3 py-2 w-full justify-center hover:bg-green-50 transition-colors mt-2"
                >
                  <i className="ri-add-line"></i> Add paragraph / list to this section
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

interface RulesSectionProps { dataKey?: string; }

export default function RulesSection({ dataKey }: RulesSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [sections, setSections] = useAdminStorage<RuleSection[]>(dataKey ?? "ma_rules", defaultRules);
  const [cta, setCta] = useAdminStorage<RulesCTAData>("ma_rules_cta", defaultRulesCTA);
  const dragIdx = useRef<number | null>(null);
  const dragOverIdx = useRef<number | null>(null);

  const handleDragStart = (idx: number) => { dragIdx.current = idx; };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    dragOverIdx.current = idx;
  };
  const handleDrop = () => {
    if (dragIdx.current === null || dragOverIdx.current === null) return;
    if (dragIdx.current === dragOverIdx.current) return;
    const reordered = [...sections];
    const [moved] = reordered.splice(dragIdx.current, 1);
    reordered.splice(dragOverIdx.current, 0, moved);
    setSections(reordered);
    dragIdx.current = null;
    dragOverIdx.current = null;
  };

  const updateTitle = useCallback((id: string, val: string) => {
    setSections(sections.map((s) => s.id === id ? { ...s, title: val } : s));
  }, [sections, setSections]);

  const updateSubHeading = useCallback((id: string, subIdx: number, val: string) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) => i === subIdx ? { ...sub, heading: val } : sub);
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const updateSubBody = useCallback((id: string, subIdx: number, val: string) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) => i === subIdx ? { ...sub, body: val } : sub);
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const updateSubListItem = useCallback((id: string, subIdx: number, itemIdx: number, val: string) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) => {
        if (i !== subIdx || !sub.list) return sub;
        const list = sub.list.map((item, li) => li === itemIdx ? val : item);
        return { ...sub, list };
      });
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const addListItem = useCallback((id: string, subIdx: number) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) => {
        if (i !== subIdx) return sub;
        return { ...sub, list: [...(sub.list || []), "New item — click to edit"] };
      });
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const removeListItem = useCallback((id: string, subIdx: number, itemIdx: number) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) => {
        if (i !== subIdx || !sub.list) return sub;
        return { ...sub, list: sub.list.filter((_, li) => li !== itemIdx) };
      });
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const addSubsection = useCallback((id: string) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      return {
        ...s,
        subsections: [
          ...s.subsections,
          { heading: "New Sub-Heading", body: "Click the pencil icon to edit this text.", list: [], listStyle: "bullet" as const },
        ],
      };
    }));
  }, [sections, setSections]);

  const removeSubsection = useCallback((id: string, subIdx: number) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      return {
        ...s,
        subsections: s.subsections.filter((_, i) => i !== subIdx),
      };
    }));
  }, [sections, setSections]);

  const removeSection = useCallback((id: string) => {
    setSections(sections.filter((s) => s.id !== id));
  }, [sections, setSections]);

  const updateSectionIcon = useCallback((id: string, icon: string, color: string, bg: string) => {
    setSections(sections.map((s) => s.id === id ? { ...s, icon, iconColor: color, iconBg: bg } : s));
  }, [sections, setSections]);

  const updateSubListIcon = useCallback((id: string, subIdx: number, icon: string, color: string) => {
    setSections(sections.map((s) => {
      if (s.id !== id) return s;
      const subsections = s.subsections.map((sub, i) =>
        i === subIdx ? { ...sub, listIcon: icon, listIconColor: color } : sub
      );
      return { ...s, subsections };
    }));
  }, [sections, setSections]);

  const addNewSection = useCallback(() => {
    const newId = `section-${Date.now()}`;
    const iconIdx = sections.length % ICON_OPTIONS.length;
    const colorIdx = sections.length % COLOR_OPTIONS.length;
    const newSection: RuleSection = {
      id: newId,
      icon: ICON_OPTIONS[iconIdx],
      iconColor: COLOR_OPTIONS[colorIdx].iconColor,
      iconBg: COLOR_OPTIONS[colorIdx].iconBg,
      title: "New Section — Click to Edit Title",
      subsections: [
        { heading: "Sub-Heading (click to edit)", body: "Click the pencil icon to edit this text. You can add bullet points, paragraphs, and more.", list: [], listStyle: "bullet" as const },
      ],
    };
    setSections([...sections, newSection]);
  }, [sections, setSections]);

  const sectionBg = isDark ? "bg-gray-950" : "#f0fdf4";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const ctaBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-pink-100";

  return (
    <section id="rules" className="py-24 px-6 transition-colors duration-300" style={isDark ? { background: "#030712" } : { background: sectionBg }}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-14">
          {(isAdmin || cta.sectionEyebrow) && (
            isAdmin ? (
              <AdminEditField value={cta.sectionEyebrow} onSave={(v) => setCta({ ...cta, sectionEyebrow: v })} onDelete={() => setCta({ ...cta, sectionEyebrow: "" })} inputClassName="text-pink-500 text-sm font-bold uppercase tracking-widest text-center">
                <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{cta.sectionEyebrow}</span>
              </AdminEditField>
            ) : (
              <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{cta.sectionEyebrow}</span>
            )
          )}
          {(isAdmin || cta.sectionHeading) && (
            isAdmin ? (
              <AdminEditField value={cta.sectionHeading} onSave={(v) => setCta({ ...cta, sectionHeading: v })} onDelete={() => setCta({ ...cta, sectionHeading: "" })} className="mt-3 mb-4" inputClassName="text-4xl font-extrabold text-center">
                <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 leading-tight ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                  {cta.sectionHeading}
                </h2>
              </AdminEditField>
            ) : (
              <h2 className={`text-4xl md:text-5xl font-extrabold mt-3 mb-4 leading-tight ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                {cta.sectionHeading}
              </h2>
            )
          )}
          {(isAdmin || cta.sectionSubtext) && (
            isAdmin ? (
              <AdminEditField value={cta.sectionSubtext} onSave={(v) => setCta({ ...cta, sectionSubtext: v })} onDelete={() => setCta({ ...cta, sectionSubtext: "" })} multiline inputClassName="text-lg text-center">
                <p className={`text-lg max-w-xl mx-auto leading-relaxed ${subColor}`}>{cta.sectionSubtext}</p>
              </AdminEditField>
            ) : (
              <p className={`text-lg max-w-xl mx-auto leading-relaxed ${subColor}`}>{cta.sectionSubtext}</p>
            )
          )}
        </div>

        <div className={`space-y-3 ${isAdmin ? "pl-8" : ""}`}>
          {sections.map((section, idx) => (
            <div
              key={section.id}
              draggable={isAdmin}
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={handleDrop}
              className={isAdmin ? "relative group/drag" : ""}
            >
              {isAdmin && (
                <div className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover/drag:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10">
                  <div className="w-6 h-10 flex items-center justify-center text-gray-400 hover:text-pink-400">
                    <i className="ri-draggable text-lg"></i>
                  </div>
                </div>
              )}
              <RuleAccordionItem
                section={section}
                isAdmin={isAdmin}
                isDark={isDark}
                onUpdateTitle={(val) => updateTitle(section.id, val)}
                onUpdateSubHeading={(si, val) => updateSubHeading(section.id, si, val)}
                onUpdateSubBody={(si, val) => updateSubBody(section.id, si, val)}
                onUpdateSubListItem={(si, li, val) => updateSubListItem(section.id, si, li, val)}
                onAddListItem={(si) => addListItem(section.id, si)}
                onRemoveListItem={(si, li) => removeListItem(section.id, si, li)}
                onAddSubsection={() => addSubsection(section.id)}
                onRemoveSubsection={(si) => removeSubsection(section.id, si)}
                onRemoveSection={() => removeSection(section.id)}
                onUpdateIcon={(icon, color, bg) => updateSectionIcon(section.id, icon, color, bg)}
                onUpdateSubListIcon={(si, icon, color) => updateSubListIcon(section.id, si, icon, color)}
              />
            </div>
          ))}
        </div>

        {isAdmin && (
          <button
            onClick={addNewSection}
            className="mt-4 w-full border-2 border-dashed border-pink-300 rounded-2xl py-5 text-pink-400 hover:text-pink-600 hover:border-pink-400 hover:bg-pink-50/50 transition-all cursor-pointer flex items-center justify-center gap-2 font-semibold text-sm"
          >
            <i className="ri-add-circle-line text-xl"></i>
            Add New Section
          </button>
        )}

        {/* CTA Box */}
        <div className={`mt-12 rounded-2xl p-8 border flex flex-col md:flex-row items-center justify-between gap-6 ${ctaBg}`}>
          <div>
            {isAdmin ? (
              <>
                <AdminEditField value={cta.heading} onSave={(v) => setCta({ ...cta, heading: v })} className="mb-1" inputClassName="font-bold text-xl">
                  <h3 className={`text-xl font-bold mb-1 ${headingColor}`}>{cta.heading}</h3>
                </AdminEditField>
                <AdminEditField value={cta.subtext} onSave={(v) => setCta({ ...cta, subtext: v })} inputClassName="text-sm">
                  <p className={`text-sm ${subColor}`}>{cta.subtext}</p>
                </AdminEditField>
              </>
            ) : (
              <>
                <h3 className={`text-xl font-bold mb-1 ${headingColor}`}>{cta.heading}</h3>
                <p className={`text-sm ${subColor}`}>{cta.subtext}</p>
              </>
            )}
          </div>
          <div className="flex gap-3 flex-shrink-0 flex-wrap">
            {isAdmin ? (
              <>
                <div className="flex items-center gap-1">
                  <AdminEditField value={cta.primaryLabel} onSave={(v) => setCta({ ...cta, primaryLabel: v })} inputClassName="font-bold text-sm">
                    <button
                      onClick={() => document.querySelector(cta.primaryHref)?.scrollIntoView({ behavior: "smooth" })}
                      className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {cta.primaryLabel}
                    </button>
                  </AdminEditField>
                  <AdminEditField value={cta.primaryHref} onSave={(v) => setCta({ ...cta, primaryHref: v })} inputClassName="text-xs">
                    <span className="text-xs text-gray-400 cursor-pointer">({cta.primaryHref})</span>
                  </AdminEditField>
                </div>
                <div className="flex items-center gap-1">
                  <AdminEditField value={cta.secondaryLabel} onSave={(v) => setCta({ ...cta, secondaryLabel: v })} inputClassName="font-semibold text-sm">
                    <button
                      onClick={() => document.querySelector(cta.secondaryHref)?.scrollIntoView({ behavior: "smooth" })}
                      className="border border-pink-300 text-pink-600 font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-pink-50 transition-colors"
                    >
                      {cta.secondaryLabel}
                    </button>
                  </AdminEditField>
                  <AdminEditField value={cta.secondaryHref} onSave={(v) => setCta({ ...cta, secondaryHref: v })} inputClassName="text-xs">
                    <span className="text-xs text-gray-400 cursor-pointer">({cta.secondaryHref})</span>
                  </AdminEditField>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => document.querySelector(cta.primaryHref)?.scrollIntoView({ behavior: "smooth" })}
                  className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer transition-colors"
                >
                  {cta.primaryLabel}
                </button>
                <button
                  onClick={() => document.querySelector(cta.secondaryHref)?.scrollIntoView({ behavior: "smooth" })}
                  className="border border-pink-300 text-pink-600 font-semibold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-pink-50 transition-colors"
                >
                  {cta.secondaryLabel}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
